import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import getLuxPlusPrimaryCashPrice, { shouldShowLuxPlusNonMemberPrice } from 'luxPlus/lib/getLuxPlusPrimaryCashPrice'
import { getIsFreePreviewLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import type { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { isGoogleHotelAdsBot } from 'selectors/accountSelectors'
import { isGhaPriceDisplay } from 'selectors/routerSelectors'

const PRICE_HEADING_VARIANTS = {
  S: 'heading6',
  M: 'heading5',
  L: 'heading4',
} as const

const SALE_UNIT_LABEL_OPTIONS = { withLeadingSlash: true } as const

interface Props {
  durationText?: ReactNode
  price: number
  memberPrice?: number
  discountPercentage?: number
  showDiscount?: boolean
  saleUnit?: string
  size?: keyof typeof PRICE_HEADING_VARIANTS
  testid?: string
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
}

function CompactSearchTilePriceBlock({
  durationText,
  price,
  memberPrice,
  discountPercentage,
  showDiscount = false,
  saleUnit,
  size = 'M',
  testid = 'search-price',
  offerConfig,
  isTour = false,
}: Props) {
  const intl = useIntl()
  const memberPriceValue = memberPrice ?? 0
  const displayedMode = useLuxPlusDisplayedMode({
    price,
    memberPrice: memberPriceValue,
    offerConfig,
    isTour,
  })
  const isGoogleHotelAds = useAppSelector(isGoogleHotelAdsBot)
  const isGhaPricing = useAppSelector(isGhaPriceDisplay)
  const isFreePreviewLuxPlusMember = useAppSelector(getIsFreePreviewLuxPlusMember)
  const canFreePreviewBuyMemberOnly = isFreePreviewLuxPlusMember && offerConfig?.restrictPurchaseToMembers === false
  const displayPrice = getLuxPlusPrimaryCashPrice(
    price,
    memberPriceValue,
    displayedMode,
    isGoogleHotelAds,
    isGhaPricing,
  )
  const showNonMemberStrike = price > displayPrice &&
    shouldShowLuxPlusNonMemberPrice(displayedMode, canFreePreviewBuyMemberOnly)
  const strikeThroughPrice = showNonMemberStrike ? price : undefined
  const priceColour = displayedMode !== 'regular-only' && displayPrice === memberPriceValue && memberPriceValue > 0 ?
    'lux-plus' :
    'normal-contrast'
  const saleUnitLabel = formatSaleUnitLabel(intl, saleUnit, SALE_UNIT_LABEL_OPTIONS)

  return <>
    {showDiscount && discountPercentage != null && (
      <PriceRowValueDiscount discountPercentage={discountPercentage} />
    )}
    {!!durationText && <Caption variant="medium" colour="neutral-two">
      {durationText}
    </Caption>}
    <Group
      data-testid={testid}
      direction="horizontal"
      gap={4}
      verticalAlign="baseline"
    >
      <Heading as="span" variant={PRICE_HEADING_VARIANTS[size]} colour={priceColour} wrap="no-wrap">
        <FormatCurrency value={displayPrice} format="roundedDollar" />
      </Heading>
      {!!saleUnitLabel && <Caption as="span" variant="medium" colour={priceColour} weight="normal">
        {saleUnitLabel}
      </Caption>}
      {strikeThroughPrice != null && <BodyText as="s" variant="medium" colour="neutral-two">
        <FormatCurrency value={strikeThroughPrice} format="roundedDollar" />
      </BodyText>}
    </Group>
  </>
}

export default CompactSearchTilePriceBlock
